import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import Text from "@/components/atoms/Text";
import Select from "@/components/atoms/Select";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { classNames } from "@/utils/Classnames";
import Spinner from "@/components/atoms/Spinner/Spinner";
import { Collection, CollectionStatus } from "@/types";
import styles from "./styles.module.css";
import { BOARD_STATUS_COLORS, buildWeek, EventCard, formatWeekRange, startOfWeek } from "../shared";

type SortBy = "site" | "driver";
export type Filter = "all" | CollectionStatus;
const STATUS_FILTERS: Filter[] = ["all", "SCHEDULED", "IN_PROGRESS", "COMPLETED"];
const LEGEND: CollectionStatus[] = ["SCHEDULED", "IN_PROGRESS", "COMPLETED"];

/**
 * Date-only Schedule board — unified calendar grid. Buckets the (already status-filtered)
 * collections into the fixed week by `planDay`, sorted within each day by site or driver.
 * Week-nav (prev/next/Today) and the Day/Week/Month/Year tabs are visual for the single
 * mock week; Sort is functional.
 */
const ScheduleBoard: FC<{
  collections: Collection[];
  selectedId: number | null;
  sortBy: SortBy;
  onSortChange: (sort: SortBy) => void;
  onSelect: (id: number) => void;
  filter: Filter;
  onFilterChange: (filter: Filter) => void;
  counts: Record<Filter, number>;
  weekStart: Dayjs;
  onWeekChange: (weekStart: Dayjs) => void;
  loading: boolean;
}> = ({
  collections,
  selectedId,
  sortBy,
  onSortChange,
  onSelect,
  filter,
  onFilterChange,
  counts,
  weekStart,
  onWeekChange,
  loading,
}) => {
  const { t } = useTranslation();

  const statusOptions = STATUS_FILTERS.map((f) => ({
    value: f,
    label: t(`containers:filter.${f}`),
  }));

  const days = useMemo(() => buildWeek(weekStart), [weekStart]);

  const byDay = useMemo(() => {
    const sorter = (a: Collection, b: Collection) =>
      sortBy === "driver"
        ? a.assignedDriverName.localeCompare(b.assignedDriverName) || a.siteName.localeCompare(b.siteName)
        : a.siteName.localeCompare(b.siteName);
    return days.map((day) =>
      collections.filter((c) => dayjs(c.plannedDate).format("YYYY-MM-DD") === day.iso).sort(sorter)
    );
  }, [collections, days, sortBy]);

  return (
    <section className={styles.boardCard}>
      {/* week-nav header */}
      <div className={styles.scheduleHeader}>
        <div className={styles.navBtnGroup}>
          <button
            type="button"
            className={styles.navBtn}
            aria-label={t("containers:schedule.prevWeek")}
            onClick={() => onWeekChange(weekStart.subtract(7, "day"))}
          >
            <SVGAsset name="chevron-right" width={13} height={13} style={{ transform: "rotate(180deg)" }} />
          </button>
          <button
            type="button"
            className={styles.navBtn}
            aria-label={t("containers:schedule.nextWeek")}
            onClick={() => onWeekChange(weekStart.add(7, "day"))}
          >
            <SVGAsset name="chevron-right" width={13} height={13} />
          </button>
        </div>
        <button type="button" className={styles.todayBtn} onClick={() => onWeekChange(startOfWeek(dayjs()))}>
          {t("containers:schedule.today")}
        </button>
        <span className={styles.weekRange}>{formatWeekRange(weekStart)}</span>

        <div className={styles.headerControls}>
          <div className={styles.controlGroup}>
            <Text span display="technical" size="xs" tt="uppercase" color="tertiary">
              {t("containers:columns.status")}
            </Text>
            <Select
              className={styles.statusSelect}
              data={statusOptions}
              size="xs"
              value={filter}
              onChange={(value) => onFilterChange((value as Filter) ?? "all")}
              allowDeselect={false}
              renderOption={({ option }) => (
                <div className={styles.statusOption}>
                  <span>{option.label}</span>
                  <span className={styles.statusOptionCount}>{counts[option.value as Filter]}</span>
                </div>
              )}
            />
          </div>
          <div className={styles.controlGroup}>
            <Text span display="technical" size="xs" tt="uppercase" color="tertiary">
              {t("containers:schedule.sort")}
            </Text>
            <button
              type="button"
              className={classNames(styles.sortBtn, sortBy === "site" ? styles.sortBtnActive : "")}
              onClick={() => onSortChange("site")}
            >
              {t("containers:schedule.sortSite")}
            </button>
            <button
              type="button"
              className={classNames(styles.sortBtn, sortBy === "driver" ? styles.sortBtnActive : "")}
              onClick={() => onSortChange("driver")}
            >
              {t("containers:schedule.sortDriver")}
            </button>
          </div>
        </div>
      </div>

      {/* unified grid frame */}
      <div className={styles.boardFrameWrap}>
        {loading && (
          <div className={styles.boardLoading}>
            <Spinner loading inline />
          </div>
        )}
        <div className={styles.boardFrame}>
          <div className={styles.boardHeadRow}>
            {days.map((day) => (
              <div
                key={day.iso}
                className={classNames(
                  styles.dayHead,
                  day.today ? styles.dayHeadToday : "",
                  day.weekend ? styles.dayHeadWeekend : ""
                )}
              >
                <div className={classNames(styles.dayWd, day.weekend ? styles.dayWdWeekend : "")}>{day.wd}</div>
                <div
                  className={
                    day.today ? styles.dayNumToday : classNames(styles.dayNum, day.weekend ? styles.dayNumWeekend : "")
                  }
                >
                  {day.dn}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.boardBodyRow}>
            {days.map((day, i) => (
              <div
                key={day.iso}
                className={classNames(
                  styles.dayBody,
                  day.today ? styles.dayBodyToday : "",
                  day.weekend ? styles.dayBodyWeekend : ""
                )}
              >
                {byDay[i].map((c) => (
                  <EventCard
                    key={c.id}
                    collection={c}
                    selected={c.id === selectedId}
                    sortedByDriver={sortBy === "driver"}
                    onSelect={() => onSelect(c.id)}
                  />
                ))}
                {byDay[i].length === 0 && (
                  <div className={styles.emptyDay}>{t("containers:schedule.noCollections")}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* legend */}
      <div className={styles.legend}>
        {LEGEND.map((s) => (
          <span key={s} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{
                ["--ev-bar" as string]: BOARD_STATUS_COLORS[s].bar,
                ["--ev-fill" as string]: BOARD_STATUS_COLORS[s].fill,
              }}
            />
            <Text span display="technical" size="xs" tt="uppercase" color="tertiary">
              {t(`containers:status.${s}`)}
            </Text>
          </span>
        ))}
      </div>
    </section>
  );
};

export default ScheduleBoard;
